import { useState, useEffect, useContext } from "react";
import {
  Container, Row, Col, Card, Badge, Button,
  Form, InputGroup, Spinner,
} from "react-bootstrap";
import FaqCreate from "./AdminFaqs";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import "../../../assets/css/admin/adminSettings/adminfaqslist.css";

const AUD_META = {
  customer: { emoji: "🧳", label: "Customer", bg: "fql-aud-customer" },
  vendor:   { emoji: "🏪", label: "Vendor",   bg: "fql-aud-vendor"   },
  general:  { emoji: "🌐", label: "General",  bg: "fql-aud-general"  },
};
const CATEGORY_LABELS = {
  booking_payments:    "Booking & Payments",
  account_login:       "Account & Login",
  tours_packages:      "Tours & Packages",
  cancellation_refund: "Cancellation & Refund",
  technical_support:   "Technical Support",
  general:              "General",
};

const STAT_CONFIG = [
  { key: "all",      emoji: "📋", label: "Total FAQs",    cls: "fql-stat-total"    },
  { key: "customer", emoji: "🧳", label: "Customer FAQs", cls: "fql-stat-customer" },
  { key: "vendor",   emoji: "🏪", label: "Vendor FAQs",   cls: "fql-stat-vendor"   },
  { key: "general",  emoji: "🌐", label: "General FAQs",  cls: "fql-stat-general"  },
];

export default function FaqManager() {
  const { baseUrl, authToken } = useContext(AppContext);

  const [view, setView] = useState("list");
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [filterAud, setFilterAud] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFaqs = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${baseUrl}/admin/faqs`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          setFaqs(response.data.data);
        } else {
          setError("Failed to load FAQs");
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Unable to connect to server");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authToken) fetchFaqs();
  }, [authToken]);

 const stats = {
  all:      faqs.length,
  customer: faqs.filter(f => (f.faq_for || '').trim().toLowerCase() === "customer").length,
  vendor:   faqs.filter(f => (f.faq_for || '').trim().toLowerCase() === "vendor").length,
  general:  faqs.filter(f => (f.faq_for || '').trim().toLowerCase() === "general").length,
};
  const filtered = faqs.filter((f) => {
    const faqFor = (f.faq_for || '').trim().toLowerCase();
    const matchAud    = filterAud === "all" || faqFor === filterAud;
    const matchStatus = filterStatus === "all" ||
      (filterStatus === "active" ? f.is_active : !f.is_active);
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (f.faq_question || '').toLowerCase().includes(q) ||
      (f.faq_answer   || '').toLowerCase().includes(q);
    return matchAud && matchStatus && matchSearch;
  });

  const handleSaved = () => {
    fetchFaqs();
    setView("list");
    setEditingFaq(null);
  };

  const handleCancel = () => {
    setView("list");
    setEditingFaq(null);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setView("edit");
  };

  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    axios
      .delete(`${baseUrl}/admin/faqs/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          fetchFaqs();
          if (expandedId === id) setExpandedId(null);
        } else {
          alert("Delete failed");
        }
      })
      .catch((err) => {
        console.error(err);
        alert("Error deleting FAQ");
      });
  };

  const handleToggleStatus = (id) => {
    axios
      .put(
        `${baseUrl}/admin/faqs/${id}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((response) => {
        if (response.data.success) fetchFaqs();
        else alert("Status update failed");
      })
      .catch((err) => {
        console.error(err);
        alert("Error updating status");
      });
  };

  if (loading) {
    return (
      <div className="fql-page">
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#FFB800' }} />
            <p className="mt-3">Loading FAQs...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fql-page">
        <Container>
          <div className="alert alert-danger">{error}</div>
          <Button className="fql-btn-primary" onClick={fetchFaqs}>Retry</Button>
        </Container>
      </div>
    );
  }

  if (view === "create" || view === "edit") {
    return (
      <>
        <div className="fql-back-bar">
          <Container>
            <Button variant="link" className="fql-back-btn" onClick={handleCancel}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: 6 }}>
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to FAQ List
            </Button>
          </Container>
        </div>
        <FaqCreate
          initialData={editingFaq}
          onCancel={handleCancel}
          onSaved={handleSaved}
        />
      </>
    );
  }

  return (
    <div className="fql-page">
      <Container>
        {/* Header */}
        <div className="fql-page-header">
          <div className="fql-header-left">
            <div className="fql-header-icon">
              <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
                <circle cx="11" cy="11" r="9" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
                <path d="M8.5 8.5C8.5 7.12 9.62 6 11 6s2.5 1.12 2.5 2.5c0 1.5-1.5 2-1.5 3.5"
                  stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="11" cy="15.5" r="0.8" fill="white"/>
              </svg>
            </div>
            <div>
              <h1 className="fql-page-title">FAQ Management</h1>
              <p className="fql-page-sub">Manage all questions & answers across audiences</p>
            </div>
          </div>
          <Button className="fql-add-btn" onClick={() => setView("create")}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 7 }}>
              <path d="M7 1v12M1 7h12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add New FAQ
          </Button>
        </div>

        <Row className="g-3 mb-4">
          {STAT_CONFIG.map(({ key, emoji, label, cls }) => (
            <Col xs={6} md={3} key={key}>
              <Card
                className={`fql-stat-card ${cls} ${filterAud === key ? "active" : ""}`}
                onClick={() => setFilterAud(key)}
              >
                <Card.Body className="d-flex align-items-center gap-3 p-3">
                  <div className="fql-stat-icon">{emoji}</div>
                  <div>
                    <div className="fql-stat-count">{stats[key]}</div>
                    <div className="fql-stat-label">{label}</div>
                  </div>
                </Card.Body>
                <div className="fql-stat-bar" />
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="fql-card">
          <Card.Header className="fql-toolbar">
            <InputGroup className="fql-search-group">
              <InputGroup.Text className="fql-search-icon-wrap">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="6" cy="6" r="4.5" stroke="#8fa3b0" strokeWidth="1.4"/>
                  <path d="M9.5 9.5l2.5 2.5" stroke="#8fa3b0" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </InputGroup.Text>
              <Form.Control
                className="fql-search"
                placeholder="Search questions or answers…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <Button variant="link" className="fql-search-clear" onClick={() => setSearch("")}>
                  ✕
                </Button>
              )}
            </InputGroup>

            <Form.Select className="fql-filter-select" value={filterAud} onChange={e => setFilterAud(e.target.value)}>
              <option value="all">All Audiences</option>
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
             <option value="general">General</option>
            </Form.Select>

            <Form.Select className="fql-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Live</option>
              <option value="draft">Draft</option>
            </Form.Select>

            <span className="fql-result-count">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </span>
          </Card.Header>

          <div className="fql-col-header">
            <span style={{ width: 36 }}>#</span>
            <span style={{ flex: 1 }}>Question</span>
            <span style={{ width: 90, textAlign: "center" }}>Audience</span>
            <span style={{ width: 64, textAlign: "center" }}>Status</span>
            <span style={{ width: 52, textAlign: "center" }}>Order</span>
            <span style={{ width: 30 }} />
          </div>

          <div className="fql-list">
            {filtered.length === 0 ? (
              <div className="fql-empty">
                <div className="fql-empty-icon">🔍</div>
                <div className="fql-empty-title">No FAQs found</div>
                <div className="fql-empty-sub">Try adjusting your search or filters</div>
              </div>
            ) : (
              filtered.map((faq, i) => {
                const faqForKey = (faq.faq_for || '').trim().toLowerCase();
                const aud = AUD_META[faqForKey] || { emoji: "❓", label: faq.faq_for || "Unknown" };
                const isOpen = expandedId === faq.id;
                const audColors = {
                  customer: { background: '#fff3cd', color: '#7a4f00' },
                  vendor:   { background: '#d4edda', color: '#1e5c2e' },
                  admin:    { background: '#ffe8b0', color: '#805500' },
                };
                const audStyle = audColors[faqForKey] || { background: '#f1f5f9', color: '#5a6a75' };
                const statusStyle = faq.is_active
                  ? { background: '#d4edda', color: '#1e5c2e', cursor: 'pointer' }
                  : { background: '#f5f5f5', color: '#666666', cursor: 'pointer' };

                return (
                  <div key={faq.id} className={`fql-row ${isOpen ? "expanded" : ""}`}>
                    <div
                      className="fql-row-main"
                      onClick={() => setExpandedId(isOpen ? null : faq.id)}
                    >
                      <span className="fql-row-index">{String(i + 1).padStart(2, "0")}</span>
                      <span className="fql-row-question">{faq.faq_question}</span>
                      <span style={{ width: 90, textAlign: "center" }}>
                        <span style={{
                          ...audStyle,
                          fontSize: 11, fontWeight: 600,
                          padding: '3px 9px', borderRadius: 20,
                          display: 'inline-block', lineHeight: 1.5
                        }}>
                          {aud.emoji} {aud.label}
                        </span>
                      </span>
                      <span style={{ width: 64, textAlign: "center" }}>
                        <span
                          style={{
                            ...statusStyle,
                            fontSize: 11, fontWeight: 600,
                            padding: '3px 9px', borderRadius: 20,
                            display: 'inline-block', lineHeight: 1.5
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(faq.id);
                          }}
                        >
                          {faq.is_active ? "● Live" : "○ Draft"}
                        </span>
                      </span>
                      <span className="fql-row-order" style={{ width: 52 }}>
                        #{faq.display_order}
                      </span>
                      <span className={`fql-chevron ${isOpen ? "up" : ""}`} style={{ width: 30 }}>
                        <svg width="11" height="7" viewBox="0 0 11 7" fill="none">
                          <path d="M1 1l4.5 4.5L10 1" stroke="#8fa3b0" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                      </span>
                    </div>

                    {isOpen && (
                      <div className="fql-row-answer">
                        {faq.faq_category && (
                          <Badge className="fql-cat-badge mb-2">
                            {CATEGORY_LABELS[faq.faq_category] || faq.faq_category}
                          </Badge>
                        )}
                        <p className="fql-answer-text">{faq.faq_answer}</p>
                        <div className="fql-answer-footer">
                          <span className="fql-answer-date">
                            Added {new Date(faq.created_at).toLocaleDateString()}
                          </span>
                          <div className="d-flex gap-2">
                            <Button size="sm" className="fql-btn-edit" onClick={() => handleEdit(faq)}>
                              <svg width="12" height="12" viewBox="0 0 13 13" fill="none" style={{ marginRight: 5 }}>
                                <path d="M9 2l2 2-7 7H2v-2L9 2z" stroke="currentColor"
                                  strokeWidth="1.3" strokeLinejoin="round"/>
                              </svg>
                              Edit
                            </Button>
                            <Button size="sm" className="fql-btn-delete" onClick={() => handleDelete(faq.id)}>
                              <svg width="12" height="12" viewBox="0 0 13 13" fill="none" style={{ marginRight: 5 }}>
                                <path d="M2 3.5h9M5 3.5V2.5h3v1M4.5 3.5l.5 7h3l.5-7"
                                  stroke="currentColor" strokeWidth="1.3"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {filtered.length > 0 && (
            <Card.Footer className="fql-footer">
              Showing {filtered.length} of {faqs.length} FAQs
            </Card.Footer>
          )}
        </Card>
      </Container>
    </div>
  );
}